import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTradeSettingsContext } from '../../contexts/TradeSettingsContext';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { PokemonCard } from '../../types/pokemon';
import { CardGrid } from '../../components/trades/CardGrid';
import { WantedCardSummary } from '../../components/trades/WantedCardSummary';
import { TradingFilters } from '../../components/trades/TradingFilters';
import { FriendCodeModal } from '../../components/modals/FriendCodeModal';
import { OwnershipFilter } from '../../components/trades/OwnershipFilterButtons';
import { PageLayout } from '../../components/layout/PageLayout';
import { Repeat, Loader2 } from 'lucide-react';
import { NotificationToggle } from '../../components/trades/NotificationToggle';
import { supabase } from '../../lib/supabase';
import { getAllDataPaginated } from '../../lib/supabaseUtils';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { Skeleton } from '../../components/ui/Skeleton';
import { SelectedCardsGridRow } from '../../components/trades/SelectedCardsGridRow';

// Modifier l'interface des filtres
interface ExtendedFilterState {
  set: string | null;
  rarity: string | null;
  search: string;
  ownership: OwnershipFilter;
  showWanted?: boolean;
  showTradable?: boolean;
}

interface UserCard {
  card_id: number;
  quantity: number;
  wanted: boolean;
  tradable: boolean;
  user_id?: string;
}

interface TradingSettings {
  id: number;
  key: string;
  value: string;
  eligible_sets?: string[];
  eligible_rarities?: string[];
}

// Ajouter la même fonction de tri des raretés
const sortRarities = (rarities: string[]) => {
  const rarityOrder: { [key: string]: number } = {
    '☆': 0,
    '♢♢♢♢': 1,
    '♢♢♢': 2,
    '♢♢': 3,
    '♢': 4
  };

  return [...rarities].sort((a, b) => {
    return (rarityOrder[a] ?? 999) - (rarityOrder[b] ?? 999);
  });
};

export const CreateTrade = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, lang, getPathForLang } = useTranslation();
  const [wantedCard, setWantedCard] = useState<PokemonCard | null>(null);
  const [offeredCards, setOfferedCards] = useState<PokemonCard[]>([]);
  const [availableCards, setAvailableCards] = useState<PokemonCard[]>([]);
  const [sets, setSets] = useState<string[]>([]);
  const [rarities, setRarities] = useState<string[]>([]);
  const [filters, setFilters] = useState<ExtendedFilterState>({
    set: null,
    rarity: null,
    search: '',
    ownership: 'all',
    showWanted: false,
    showTradable: false
  });
  const [offeredCardsFilters, setOfferedCardsFilters] = useState<ExtendedFilterState>({
    set: null,
    rarity: null,
    search: '',
    ownership: 'all',
    showWanted: false,
    showTradable: false
  });
  const [showFriendCodeModal, setShowFriendCodeModal] = useState(false);
  const [cardQuantities, setCardQuantities] = useState<Record<number, number>>({});
  const [emailNotification, setEmailNotification] = useState<boolean>(true);
  const { settings } = useTradeSettingsContext();
  const [userCards, setUserCards] = useState<UserCard[]>([]);
  const [autoTradeSuggestions, setAutoTradeSuggestions] = useState<Array<{
    wantedCard: PokemonCard,
    tradableCards: PokemonCard[]
  }>>([]);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [activeTradeWantedCards, setActiveTradeWantedCards] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Déplacer les fonctions en dehors des useEffect pour les rendre réutilisables
  const loadTradingSettings = async (settings: TradingSettings[] | null | undefined) => {
    if (settings && Array.isArray(settings) && settings.length > 0) {
      const tradingSettings = settings[0];
      setSets(tradingSettings.eligible_sets || []);
      setRarities(sortRarities(tradingSettings.eligible_rarities || []));
    }
  };

  const loadCardQuantities = async (user: { id: string } | null) => {
    if (!user) return;

    // Utiliser la pagination manuelle pour récupérer toutes les cartes utilisateur
    const allUserCards = [];
    let from = 0;
    const batchSize = 1000;
    
    while (true) {
      const { data: batch, error } = await supabase
        .from('user_cards')
        .select('card_id, quantity, wanted, tradable')
        .eq('user_id', user.id)
        .range(from, from + batchSize - 1);
      
      if (error) {
        console.error('Erreur lors du chargement des cartes utilisateur:', error);
        break;
      }
      
      if (!batch || batch.length === 0) break;
      
      allUserCards.push(...batch);
      
      if (batch.length < batchSize) break;
      from += batchSize;
    }
    
    const data = allUserCards;

    if (data) {
      const quantities = data.reduce((acc, { card_id, quantity }) => {
        acc[card_id] = quantity;
        return acc;
      }, {} as Record<number, number>);
      setCardQuantities(quantities);
      setUserCards(data);
    }
  };

  const loadUserCards = async (user: { id: string } | null) => {
    if (!user) return;

    // Utiliser la pagination manuelle pour récupérer toutes les cartes utilisateur
    const allUserCards = [];
    let from = 0;
    const batchSize = 1000;
    
    while (true) {
      const { data: batch, error } = await supabase
        .from('user_cards')
        .select('card_id, quantity, wanted, tradable')
        .eq('user_id', user.id)
        .range(from, from + batchSize - 1);
      
      if (error) {
        console.error('Erreur lors du chargement des cartes utilisateur:', error);
        break;
      }
      
      if (!batch || batch.length === 0) break;
      
      allUserCards.push(...batch);
      
      if (batch.length < batchSize) break;
      from += batchSize;
    }
    
    const data = allUserCards;

    if (data) {
      setUserCards(data);
    }
  };

  const loadActiveTradeWantedCards = async (user: { id: string } | null) => {
    if (!user) return;

    const { data: activeTradesData } = await supabase
      .from('trades')
      .select('wanted_card_id')
      .eq('user_id', user.id)
      .in('status', ['pending', 'accepted']);

    if (activeTradesData) {
      setActiveTradeWantedCards(activeTradesData.map(trade => trade.wanted_card_id));
    }
  };

  // Charger toutes les cartes disponibles pour l'échange
  useEffect(() => {
    const loadAvailableCards = async () => {
      try {
        if (!sets.length || !rarities.length) {
          setAvailableCards([]);
          return;
        }

        // Utiliser getAllDataPaginated pour contourner la limitation de 1000 résultats
        console.log('Chargement de toutes les cartes avec pagination...');
        const allCards = await getAllDataPaginated('cards', '*');
        
        // Filtrer côté client pour obtenir les cartes correspondant aux critères
        const data = allCards.filter(card => 
          sets.includes(card.set) && rarities.includes(card.rarity)
        );
        
        console.log(`Cartes filtrées: ${data.length} sur ${allCards.length} cartes totales`);
        setAvailableCards(data || []);
      } catch (error) {
        console.error('Error loading available cards:', error);
        setAvailableCards([]);
      }
    };

    loadAvailableCards();
  }, [sets, rarities]);

  const handleOfferedCardClick = (card: PokemonCard) => {
    setOfferedCards(prev => {
      const isCardSelected = prev.some(c => c.id === card.id);
      if (isCardSelected) {
        return prev.filter(c => c.id !== card.id);
      } else {
        return [...prev, card];
      }
    });
  };

  // Modifier cette fonction pour réinitialiser les cartes sélectionnées
  const handleWantedCardChange = () => {
    // Réinitialiser les cartes offertes quand on change la carte recherchée
    setOfferedCards([]);
    setWantedCard(null);
  };

  const handleCreateTrade = async () => {
    if (!user || !wantedCard || offeredCards.length === 0 || isCreating) return;

    try {
      setError(null); // Réinitialiser l'erreur
      setIsCreating(true);

      const { data: profile } = await supabase
        .from('profiles')
        .select('pokemon_id, game_pseudo')
        .eq('user_id', user.id)
        .single();

      if (!profile?.pokemon_id || profile.pokemon_id.trim() === '' || !profile?.game_pseudo || profile.game_pseudo.trim() === '') {
        setShowFriendCodeModal(true);
        return;
      }

      const { data: trade, error: tradeError } = await supabase
        .from('trades')
        .insert({
          user_id: user.id,
          wanted_card_id: wantedCard.id,
          status: 'pending',
          creator_pseudo: profile.game_pseudo,
          mail_notification: emailNotification
        })
        .select()
        .single();

      if (tradeError) {
        if (tradeError.code === '23505') {
          setError("Vous avez déjà un échange actif pour cette carte");
          return;
        }
        throw tradeError;
      }

      const { error: cardsError } = await supabase
        .from('trade_cards')
        .insert(
          offeredCards.map(card => ({
            trade_id: trade.id,
            card_id: card.id
          }))
        );

      if (cardsError) throw cardsError;

      navigate(getPathForLang(`/trades/${trade.id}/info`));
    } catch (error) {
      console.error('Error creating trade:', error);
      setError("Une erreur est survenue lors de la création de l'échange");
    } finally {
      setIsCreating(false);
    }
  };

  // Modifier les fonctions de filtrage
  const filteredWantedCards = useMemo(() => {
    return availableCards.filter(card => {
      const matchesSet = !filters.set || card.set === filters.set;
      const matchesRarity = !filters.rarity || card.rarity === filters.rarity;
      const matchesSearch = !filters.search || (
        lang === 'en' 
          ? (card.card_en?.toLowerCase().includes(filters.search.toLowerCase()) || 
             card.card_fr?.toLowerCase().includes(filters.search.toLowerCase()))
          : card.card_fr?.toLowerCase().includes(filters.search.toLowerCase())
      );
      
      const quantity = cardQuantities[card.id] || 0;
      const userCard = userCards?.find(uc => uc.card_id === card.id);
      
      const matchesOwnership = filters.ownership === 'all' ||
        (filters.ownership === 'not_owned' && quantity === 0) ||
        (filters.ownership === 'owned' && quantity <= 1);

      const matchesWanted = !filters.showWanted || userCard?.wanted;

      return matchesSet && matchesRarity && matchesSearch && matchesOwnership && matchesWanted;
    });
  }, [availableCards, filters, cardQuantities, userCards, lang]);

  const filteredOfferedCards = useMemo(() => {
    return availableCards.filter(card => {
      // Exclure la carte demandée des cartes proposées
      if (wantedCard && card.id === wantedCard.id) {
        return false;
      }

      // Vérifier que la rareté correspond
      if (wantedCard && card.rarity !== wantedCard.rarity) {
        return false;
      }

      const matchesSet = !offeredCardsFilters.set || card.set === offeredCardsFilters.set;
      const matchesSearch = !offeredCardsFilters.search || (
        lang === 'en'
          ? (card.card_en?.toLowerCase().includes(offeredCardsFilters.search.toLowerCase()) ||
             card.card_fr?.toLowerCase().includes(offeredCardsFilters.search.toLowerCase()))
          : card.card_fr?.toLowerCase().includes(offeredCardsFilters.search.toLowerCase())
      );
      
      const quantity = cardQuantities[card.id] || 0;
      const userCard = userCards?.find(uc => uc.card_id === card.id);
      const matchesOwnership = offeredCardsFilters.ownership === 'all' ||
        (offeredCardsFilters.ownership === 'owned' && quantity > 0) ||
        (offeredCardsFilters.ownership === 'duplicates' && quantity >= 3);

      const matchesTradable = !offeredCardsFilters.showTradable || userCard?.tradable;

      return matchesSet && matchesSearch && matchesOwnership && matchesTradable;
    });
  }, [availableCards, offeredCardsFilters, cardQuantities, wantedCard, userCards, lang]);

  // Ajouter un effet pour vérifier le code ami
  useEffect(() => {
    const checkProfileInfo = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('pokemon_id, game_pseudo')
        .eq('user_id', user.id)
        .single();

      // Vérifier si le code ami ou le pseudo du jeu existe ET n'est pas vide
      setShowFriendCodeModal(!data?.pokemon_id || data.pokemon_id.trim() === '' || !data?.game_pseudo || data.game_pseudo.trim() === '');
    };

    checkProfileInfo();
  }, [user]);

  // Ajouter la fonction de callback pour la modale
  const handleFriendCodeSuccess = () => {
    setShowFriendCodeModal(false);
  };

  // Modifier la fonction generateAutoTradeSuggestions pour gérer le chargement
  const generateAutoTradeSuggestions = useCallback(() => {
    if (!availableCards.length || !userCards.length) return;

    setIsSuggestionsLoading(true);
    try {
      const wantedUserCards = userCards.filter(uc => 
        uc.wanted && !activeTradeWantedCards.includes(uc.card_id)
      );
      const tradableUserCards = userCards.filter(uc => uc.tradable);

      const suggestions: Array<{
        wantedCard: PokemonCard,
        tradableCards: PokemonCard[]
      }> = [];

      wantedUserCards.forEach(wantedUC => {
        const wantedCard = availableCards.find(c => c.id === wantedUC.card_id);
        if (!wantedCard) return;

        const matchingTradableCards = tradableUserCards
          .map(tradableUC => ({
            userCard: tradableUC,
            card: availableCards.find(c => c.id === tradableUC.card_id)
          }))
          .filter(({ card }) => card && card.rarity === wantedCard.rarity)
          .map(({ card }) => card!);

        if (matchingTradableCards.length > 0) {
          suggestions.push({
            wantedCard,
            tradableCards: matchingTradableCards
          });
        }
      });

      setAutoTradeSuggestions(suggestions);
    } finally {
      setIsSuggestionsLoading(false);
    }
  }, [availableCards, userCards, activeTradeWantedCards]);

  // Ajouter l'effet pour générer les suggestions
  useEffect(() => {
    generateAutoTradeSuggestions();
  }, [generateAutoTradeSuggestions]);

  // Ajouter la fonction pour appliquer une suggestion
  const handleApplySuggestion = (suggestion: typeof autoTradeSuggestions[0]) => {
    setWantedCard(suggestion.wantedCard);
    setOfferedCards(suggestion.tradableCards);
  };

  // Modifier l'effet qui charge les données initiales
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          loadTradingSettings(settings),
          loadCardQuantities(user),
          loadUserCards(user),
          loadActiveTradeWantedCards(user)
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadInitialData();
    }
  }, [user, settings]);

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('trades.breadcrumb.trades'), path: '/trades/browse', icon: <Repeat className="w-4 h-4" /> },
          { label: t('trade_choice.create_trade.title'), path: '/trades/create' }
        ]
      }}
      className="!p-0 sm:!p-2 md:!p-4"
    >
      <div className="max-w-3xl md:max-w-4xl lg:max-w-6xl mx-auto bg-white shadow-sm rounded-lg overflow-hidden mt-8 sm:mt-4 md:mt-6">
        {/* En-tête de la page */}
        <div className="border-b border-gray-200 bg-white px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('create_trade.page_title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('create_trade.subtitle')}
          </p>
        </div>

        {/* Section des suggestions avec skeleton */}
        {!wantedCard && (isSuggestionsLoading || autoTradeSuggestions.length > 0) && (
          <div className="border-b border-gray-200 p-3 sm:p-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
            {isSuggestionsLoading ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
                <div className="space-y-3">
                  {[...Array(1)].map((_, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <Skeleton className="w-12 sm:w-16 aspect-[2/3] rounded-md" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </div>
                        <Skeleton className="w-20 h-8 rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 xl:grid-cols-16 gap-1">
                          {[...Array(12)].map((_, cardIndex) => (
                            <Skeleton key={cardIndex} className="aspect-[2/3] rounded-md" />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : autoTradeSuggestions.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-blue-500" />
                      {t('create_trade.suggestions.title')}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      {t('create_trade.suggestions.subtitle')}
                    </p>
                  </div>
                  {autoTradeSuggestions.length > 1 && (
                    <button
                      onClick={() => setShowAllSuggestions(!showAllSuggestions)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs sm:text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
                    >
                      {showAllSuggestions 
                        ? t('create_trade.suggestions.view_less')
                        : t('create_trade.suggestions.view_more', { count: autoTradeSuggestions.length - 1 })}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {(showAllSuggestions ? autoTradeSuggestions : autoTradeSuggestions.slice(0, 1)).map((suggestion, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded-lg shadow-sm border border-gray-100 hover:border-blue-200 transition-colors"
                    >
                      <div className="p-3">
                        {/* En-tête avec la carte souhaitée et le bouton */}
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 w-12 sm:w-16 relative group">
                              <div className="relative aspect-[2/3]">
                                <img
                                  src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}preview/${suggestion.wantedCard.image || ''}`}
                                  alt={suggestion.wantedCard.card_fr}
                                  className="absolute inset-0 w-full h-full object-contain rounded-md shadow-sm group-hover:shadow-md transition-shadow"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-md" />
                              </div>
                              <div className="absolute -top-1 -right-1">
                                <div className="bg-blue-100 text-blue-600 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                  {t('create_trade.filters.show_wanted')}
                                </div>
                              </div>
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-medium text-gray-900 truncate">
                                {suggestion.wantedCard.card_fr}
                              </h3>
                              <p className="text-xs text-gray-500 truncate">
                                {suggestion.wantedCard.set} • {suggestion.wantedCard.rarity}
                              </p>
                            </div>
                          </div>

                          <button
                            onClick={() => handleApplySuggestion(suggestion)}
                            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors shadow-sm"
                          >
                            <span className="hidden sm:inline">{t('create_trade.actions.create_trade')}</span>
                            <span className="sm:hidden">{t('create_trade.suggestions.create')}</span>
                            <Repeat className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Grille des cartes disponibles */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                              <span>{t('create_trade.suggestions.available_tradable')}</span>
                              <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-[10px]">
                                {t('create_trade.suggestions.count', { count: suggestion.tradableCards.length })}
                              </span>
                            </h4>
                          </div>
                          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 xl:grid-cols-16 gap-1">
                            {suggestion.tradableCards.map((card, cardIndex) => (
                              <div 
                                key={cardIndex} 
                                className="relative aspect-[2/3] cursor-pointer group"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApplySuggestion({
                                    wantedCard: suggestion.wantedCard,
                                    tradableCards: [card]
                                  });
                                }}
                              >
                                <img
                                  src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}preview/${card.image || ''}`}
                                  alt={card.card_fr}
                                  className="absolute inset-0 w-full h-full object-contain rounded-md shadow-sm group-hover:shadow-md transition-all group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-md" />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Contenu principal avec skeleton */}
        <div className="p-4 sm:p-6 space-y-6 bg-gray-50">
          {isLoading ? (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Skeleton className="w-8 h-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-64" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-3 sm:p-6 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <Skeleton className="h-9 rounded-md" />
                  <Skeleton className="h-9 rounded-md" />
                  <Skeleton className="h-9 rounded-md" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {[...Array(12)].map((_, index) => (
                    <Skeleton key={index} className="aspect-[2/3] rounded-lg" />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            !wantedCard ? (
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-medium text-gray-900">
                      {t('create_trade.step1.title')}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('create_trade.step1.subtitle')}
                    </p>
                  </div>
                </div>
                
                <div className="bg-white shadow rounded-lg p-3 sm:p-6">
                  <TradingFilters
                    filters={filters}
                    onFilterChange={setFilters}
                    sets={sets}
                    rarities={rarities}
                    isWantedCardSection={true}
                  />

                  <div className="mt-3 sm:mt-4">
                    <CardGrid
                      cards={filteredWantedCards}
                      selectedCards={wantedCard ? [wantedCard] : []}
                      cardQuantities={cardQuantities}
                      onCardClick={setWantedCard}
                      className="gap-2 sm:gap-4"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-medium text-gray-900">
                      {t('create_trade.step2.title')}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('create_trade.step2.subtitle')}
                    </p>
                  </div>
                </div>

                <WantedCardSummary 
                  card={wantedCard} 
                  className="mb-4" 
                  onChangeCard={handleWantedCardChange}
                />

                <div className="bg-white shadow rounded-lg p-3 sm:p-6">
                  <TradingFilters
                    filters={offeredCardsFilters}
                    onFilterChange={setOfferedCardsFilters}
                    sets={sets}
                    rarities={[]}
                    wantedCardRarity={wantedCard.rarity || undefined}
                    isWantedCardSection={false}
                  />

                  {/* Afficher le récapitulatif des cartes sélectionnées si au moins une carte est sélectionnée */}
                  {offeredCards.length > 0 && (
                    <SelectedCardsGridRow
                      cards={offeredCards}
                      onRemoveCard={handleOfferedCardClick}
                      cardQuantities={cardQuantities}
                      title={t('create_trade.selected_cards')}
                      className="mb-4 mt-3"
                    />
                  )}
                  
                  <div className="mt-3 sm:mt-4">
                    <CardGrid
                      cards={filteredOfferedCards}
                      selectedCards={offeredCards}
                      cardQuantities={cardQuantities}
                      onCardClick={handleOfferedCardClick}
                      className="gap-2 sm:gap-4"
                      showSelectAll={true}
                      onSelectAll={(cards) => setOfferedCards(cards)}
                    />
                  </div>
                </div>

                {/* Footer avec notification et bouton de création */}
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <NotificationToggle 
                    value={emailNotification}
                    onChange={setEmailNotification}
                    defaultValue={true}
                  />

                  <div className="flex flex-col gap-3 mt-4">
                    {error && (
                      <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200">
                        <div className="flex items-center gap-2 text-red-700">
                          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium">{error === "Vous avez déjà un échange actif pour cette carte" ? t('create_trade.error.existing_trade') : t('create_trade.error.generic')}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={handleCreateTrade}
                        disabled={offeredCards.length === 0 || isCreating}
                        className={`w-full sm:w-auto px-4 sm:px-6 py-2 rounded-md text-white font-medium
                          ${(offeredCards.length === 0 || isCreating)
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-blue-600 hover:bg-blue-700'}`}
                      >
                        {isCreating ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('create_trade.actions.creating')}
                          </span>
                        ) : (
                          t('create_trade.actions.create_trade')
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <FriendCodeModal
        isOpen={showFriendCodeModal}
        onClose={() => setShowFriendCodeModal(false)}
        onSuccess={handleFriendCodeSuccess}
      />
    </PageLayout>
  );
}; 